import {
  capitalizeString,
  formatSalesOrderNumber,
  formatWorkOrderNumber,
} from '@zspace/format';
import {
  DeviceGroupPermissions,
  SoftwareSeatPermissions,
  WorkOrderPermissions,
} from '@zspace/roles';
import { WorkOrderStatus, WorkOrderSummaryData } from '@zspace/types';
import CheckPermissions from '../../shared/check-permissions/check-permissions';
import { formatDateToLocaleString } from '../../shared/utils';
import Link from '../../ui/link/link';
import PaginatedTable, {
  CustomPaginatedTableProps,
} from '../../ui/table/paginated-table/paginated-table';
import { Column } from '../../ui/table/types';
import ViewAllList from '../../ui/view-all-list/view-all-list';

export type WorkOrdersTableProps =
  CustomPaginatedTableProps<WorkOrderSummaryData>;

function getWorkOrderDetailsPath(workOrder: WorkOrderSummaryData) {
  if (workOrder.status === WorkOrderStatus.DRAFT) {
    return `/work-orders/${workOrder.id}/device-groups`;
  }

  return `/work-orders/${workOrder.id}`;
}

const columns: Column<WorkOrderSummaryData>[] = [
  {
    key: 'number',
    className: 'is-width-15',
    render: (el) => (
      <CheckPermissions
        permissions={[
          DeviceGroupPermissions.DEVICE_GROUPS_READ,
          SoftwareSeatPermissions.SOFTWARE_SEATS_READ,
        ]}
      >
        <CheckPermissions.Render>
          <Link to={getWorkOrderDetailsPath(el)}>
            {formatWorkOrderNumber(el.number)}
          </Link>
        </CheckPermissions.Render>
        <CheckPermissions.Fallback>
          <span className="has-text-primary-dark">
            {formatWorkOrderNumber(el.number)}
          </span>
        </CheckPermissions.Fallback>
      </CheckPermissions>
    ),
    title: 'WO Number',
    sortable: true,
  },
  {
    key: 'name',
    className: 'is-width-15',
    render: (el) => (
      <CheckPermissions
        permissions={[WorkOrderPermissions.WORK_ORDERS_DETAILS_READ]}
      >
        <CheckPermissions.Render>
          <Link to={getWorkOrderDetailsPath(el)}>{el.name}</Link>
        </CheckPermissions.Render>
        <CheckPermissions.Fallback>
          <span className="has-text-primary-dark">{el.name}</span>
        </CheckPermissions.Fallback>
      </CheckPermissions>
    ),
    title: 'WO Name',
    sortable: true,
  },
  {
    key: 'salesOrders.opportunity.account.name',
    className: 'is-width-15',
    render: (el) => (
      <ViewAllList
        list={el.salesOrders
          .filter((so) => so.opportunity)
          .map((so) => so.opportunity?.account)}
        horizontalList
        renderItem={(account) => (
          <Link to={`/accounts/${account?.id}`}>{account?.name}</Link>
        )}
        separator=", "
      />
    ),
    title: 'Accounts',
    sortable: true,
  },
  {
    key: 'salesOrders',
    className: 'is-width-10',
    render: (el) => (
      <ViewAllList
        list={el.salesOrders}
        renderItem={(el) => <p>{formatSalesOrderNumber(el.number)}</p>}
      />
    ),
    title: 'Sales orders',
    sortable: false,
  },
  {
    key: 'type',
    className: 'is-width-10',
    render: (el) => capitalizeString(el.type).replace('_', ' '),
    title: 'Type',
    sortable: false,
  },
  {
    key: 'devicesNumber',
    className: 'is-width-10',
    render: (el) => {
      return el.devices.total && el.devices.total > 0
        ? `${el.devices.unassigned}/${el.devices.total}`
        : 'N/A';
    },
    title: 'Available devices',
    sortable: false,
  },
  {
    key: 'complex',
    className: 'is-width-5',
    render: (el) => (el.complex ? 'Yes' : 'No'),
    title: 'Complex',
    sortable: false,
  },
  {
    key: 'createdBy',
    className: 'is-width-15',
    title: 'Created by',
    dataIndex: 'createdBy',
    sortable: false,
  },
  {
    key: 'createdAt',
    className: 'is-width-5',
    render: (el) => formatDateToLocaleString(el.createdAt),
    title: 'Date',
    sortable: true,
  },
];

export function WorkOrdersTable({
  dataSource,
  onChange,
  config,
  form,
  empty,
  children,
}: WorkOrdersTableProps) {
  return (
    <PaginatedTable
      dataSource={dataSource}
      columns={columns}
      onChange={onChange}
      config={config}
      form={form}
      empty={empty}
      rowKey={'id'}
      children={children}
    />
  );
}

export default WorkOrdersTable;
